import { useRef } from "react";
import { scopedThreadKey, scopeProjectRef, scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { SidebarThreadSortOrder } from "@cmmd-forge/contracts/settings";
import {
  selectSidebarThreadsForProjectRef,
  selectSidebarThreadsForProjectRefs,
  useStore,
} from "../../store";
import { useUiStateStore } from "../../uiStateStore";
import { sortThreads } from "../../lib/threadSort";
import { resolveProjectStatusIndicator, resolveThreadStatusPill } from "../Sidebar.logic";
import type { SidebarThreadSummary } from "../../types";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import {
  computeVisibleProjectThreads,
  selectTopLevelProjectThreads,
  selectProjectThreadsFromSource,
} from "./useSidebarProjectThreadData.logic";

// ---------------------------------------------------------------------------
// useSidebarProjectThreadData — store subscriptions and computed thread lists
//
// IMPORTANT: every thread that belongs to ANY checkout of a logical
// project shows up under that project's row, regardless of which
// worktree the user has focused in the switcher. This matches Codex /
// Claude semantics — a project is a stable bucket of conversations, the
// worktree switcher only selects "where new work runs," not "what
// threads I can see." Filtering threads by worktree was disorienting:
// users would lose sight of in-flight threads simply because they
// switched checkouts.
// ---------------------------------------------------------------------------

const THREAD_PREVIEW_LIMIT = 6;

export interface UseSidebarProjectThreadDataParams {
  readonly project: SidebarProjectSnapshot;
  readonly threadSortOrder: SidebarThreadSortOrder;
  readonly isThreadListExpanded: boolean;
  readonly activeRouteThreadKey: string | null;
  readonly excludedThreadKeys?: ReadonlySet<string>;
  readonly threadSource?: readonly SidebarThreadSummary[];
}

export function useSidebarProjectThreadData(params: UseSidebarProjectThreadDataParams) {
  const { project, threadSortOrder, isThreadListExpanded, excludedThreadKeys, threadSource } =
    params;

  const activeEnvironmentId = useStore((state) => state.activeEnvironmentId);
  const environmentStateById = useStore((state) => state.environmentStateById);
  const storeSnapshot = { activeEnvironmentId, environmentStateById };
  const sidebarThreads = selectSidebarThreadsForProjectRef(
    storeSnapshot,
    scopeProjectRef(project.environmentId, project.id),
  );

  const otherMemberRefs = project.memberProjectRefs.filter(
    (ref) => ref.environmentId !== project.environmentId || ref.projectId !== project.id,
  );

  const otherMemberThreads =
    otherMemberRefs.length === 0
      ? ([] as SidebarThreadSummary[])
      : selectSidebarThreadsForProjectRefs(storeSnapshot, otherMemberRefs);

  const allSidebarThreads = (() => {
    if (threadSource) {
      return selectProjectThreadsFromSource({
        threads: threadSource,
        projectRef: scopeProjectRef(project.environmentId, project.id),
        memberProjectRefs: project.memberProjectRefs,
      });
    }
    return otherMemberThreads.length === 0
      ? sidebarThreads
      : [...sidebarThreads, ...otherMemberThreads];
  })();

  // Archived threads keep their projection rows so they can be restored later,
  // but the sidebar treats `archived_at != null` as "hidden from the active
  // list". Without this filter the row keeps rendering after Archive even
  // though the DB write succeeded, and users see Archive as a silent no-op.
  const projectThreads = computeVisibleProjectThreads(allSidebarThreads, excludedThreadKeys);
  const topLevelProjectThreads = selectTopLevelProjectThreads(projectThreads);

  const sidebarThreadByKey = new Map(
    projectThreads.map(
      (thread) =>
        [scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)), thread] as const,
    ),
  );

  const sidebarThreadByKeyRef = useRef(sidebarThreadByKey);
  sidebarThreadByKeyRef.current = sidebarThreadByKey;

  const projectExpanded = useUiStateStore(
    (state) => state.projectExpandedById[project.projectKey] ?? true,
  );
  const threadLastVisitedAtById = useUiStateStore((state) => state.threadLastVisitedAtById);
  const threadLastVisitedAts = topLevelProjectThreads.map(
    (thread) =>
      threadLastVisitedAtById[scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id))] ??
      null,
  );

  const { projectStatus, visibleProjectThreads, orderedProjectThreadKeys } = (() => {
    const lastVisitedAtByThreadKey = new Map(
      topLevelProjectThreads.map((thread, index) => [
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        threadLastVisitedAts[index] ?? null,
      ]),
    );
    const resolveStatus = (thread: SidebarThreadSummary) => {
      const lastVisitedAt = lastVisitedAtByThreadKey.get(
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      );
      return resolveThreadStatusPill({
        thread: {
          ...thread,
          ...(lastVisitedAt !== null && lastVisitedAt !== undefined ? { lastVisitedAt } : {}),
        },
      });
    };
    const visible = sortThreads(topLevelProjectThreads, threadSortOrder);
    return {
      orderedProjectThreadKeys: visible.map((t) =>
        scopedThreadKey(scopeThreadRef(t.environmentId, t.id)),
      ),
      projectStatus: resolveProjectStatusIndicator(visible.map(resolveStatus)),
      visibleProjectThreads: visible,
    };
  })();

  const threadPanelState = (() => {
    const lastVisitedAtByThreadKey = new Map(
      topLevelProjectThreads.map((thread, index) => [
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        threadLastVisitedAts[index] ?? null,
      ]),
    );
    const resolveStatus = (thread: SidebarThreadSummary) => {
      const lastVisitedAt = lastVisitedAtByThreadKey.get(
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      );
      return resolveThreadStatusPill({
        thread: {
          ...thread,
          ...(lastVisitedAt !== null && lastVisitedAt !== undefined ? { lastVisitedAt } : {}),
        },
      });
    };
    const hasOverflowingThreads = visibleProjectThreads.length > THREAD_PREVIEW_LIMIT;
    const previewThreads =
      isThreadListExpanded || !hasOverflowingThreads
        ? visibleProjectThreads
        : visibleProjectThreads.slice(0, THREAD_PREVIEW_LIMIT);
    const visibleThreadKeys = new Set(
      previewThreads.map((t) => scopedThreadKey(scopeThreadRef(t.environmentId, t.id))),
    );
    const renderedThreads = projectExpanded
      ? visibleProjectThreads.filter((t) =>
          visibleThreadKeys.has(scopedThreadKey(scopeThreadRef(t.environmentId, t.id))),
        )
      : [];
    const hiddenThreads = visibleProjectThreads.filter(
      (t) => !visibleThreadKeys.has(scopedThreadKey(scopeThreadRef(t.environmentId, t.id))),
    );
    return {
      hasOverflowingThreads,
      hiddenThreadStatus: resolveProjectStatusIndicator(hiddenThreads.map(resolveStatus)),
      renderedThreads,
      showEmptyThreadState: projectExpanded && visibleProjectThreads.length === 0,
      shouldShowThreadPanel: projectExpanded,
    };
  })();

  return {
    projectThreads,
    projectExpanded,
    sidebarThreadByKeyRef,
    projectStatus,
    orderedProjectThreadKeys,
    ...threadPanelState,
  };
}
